import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { Plus, Trash2, Save, CreditCard, AlertTriangle } from 'lucide-react';

const TIPI = {
  BONIFICO: 'Bonifico bancario',
  CONTANTI: 'Contanti in ufficio',
  LINK: 'Link esterno',
};

const classeInput =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-900 disabled:bg-gray-50 disabled:text-gray-500';

/* Chiave locale delle schede non ancora salvate: prima che il
   database dia loro un id, React ha bisogno di distinguerle. */
let contatore = 0;
const chiave = () => `m${++contatore}`;

const CAMPI = ['nome', 'tipo', 'istruzioni', 'iban', 'intestatario',
               'causale_template', 'url', 'attivo'];

const METODO_VUOTO = () => ({
  _k: chiave(), nuovo: true, nome: '', tipo: 'BONIFICO', istruzioni: '',
  iban: '', intestatario: '', causale_template: '{codice_ordine} - {evento}',
  url: '', attivo: true,
});

const ImpostazioniEventi = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);

  const [impostazioni, setImpostazioni] = useState(null);
  const [metodi, setMetodi] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [salvataggio, setSalvataggio] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const [cfg, elenco] = await Promise.all([
        api.get('/navette/impostazioni/'),
        getAll('/navette/metodi-pagamento/'),
      ]);
      setImpostazioni(cfg.data);
      setMetodi(elenco.map((m) => ({ ...m, _k: `s${m.id}` })));
    } catch (errore) {
      console.error('Errore caricamento impostazioni:', errore);
      notify.error('Errore nel caricamento');
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const salvaImpostazioni = async () => {
    setSalvataggio(true);
    try {
      await api.put('/navette/impostazioni/', impostazioni);
      notify.success('Impostazioni salvate');
    } catch (errore) {
      notify.error('Salvataggio non riuscito');
    } finally {
      setSalvataggio(false);
    }
  };

  /* "Aggiungi" apre una scheda vuota a schermo e basta. Scriverla
     subito nel database lasciava in giro un "Nuovo metodo" senza IBAN
     ogni volta che qualcuno ci ripensava. */
  const aggiungiMetodo = () => setMetodi((v) => [...v, METODO_VUOTO()]);

  const salvaMetodo = async (metodo, bozza, indice) => {
    const corpo = {
      nome: (bozza.nome || '').trim(),
      tipo: bozza.tipo,
      istruzioni: bozza.istruzioni || '',
      iban: bozza.iban || '',
      intestatario: bozza.intestatario || '',
      causale_template: bozza.causale_template || '',
      url: bozza.url || '',
      attivo: !!bozza.attivo,
      ordine: indice,
    };
    if (!corpo.nome) return notify.error('Dai un nome al metodo: lo legge il cliente.');
    try {
      if (metodo.nuovo) await api.post('/navette/metodi-pagamento/', corpo);
      else await api.patch(`/navette/metodi-pagamento/${metodo.id}/`, corpo);
      notify.success('Metodo salvato');
      carica();
    } catch (errore) {
      notify.error(primoErrore(errore) || 'Salvataggio non riuscito');
    }
  };

  const eliminaMetodo = async (metodo) => {
    // Una scheda mai salvata si chiude e basta: non c'e' niente da
    // cancellare e non ha senso chiedere conferma.
    if (metodo.nuovo) {
      setMetodi((v) => v.filter((m) => m._k !== metodo._k));
      return;
    }
    if (!window.confirm(`Eliminare "${metodo.nome}"?`)) return;
    try {
      await api.delete(`/navette/metodi-pagamento/${metodo.id}/`);
      carica();
    } catch (errore) {
      notify.error(primoErrore(errore) || 'Eliminazione non riuscita');
    }
  };

  if (caricamento || !impostazioni) {
    return <AdminLayout><div className="p-6 text-gray-400">Carico…</div></AdminLayout>;
  }

  const campo = (nome, valore) => setImpostazioni((i) => ({ ...i, [nome]: valore }));
  // Una scheda aperta a schermo non e' un metodo attivo: l'avviso deve
  // restare finche' non c'e' davvero qualcosa di salvato.
  const nessunMetodoAttivo = metodi.filter((m) => m.attivo && !m.nuovo).length === 0;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-3xl">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Impostazioni navette</h1>
            <p className="text-sm text-gray-500 mt-0.5">Tempi di pagamento e modi di pagare</p>
          </div>
          {modificabile && (
            <button
              onClick={salvaImpostazioni}
              disabled={salvataggio}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {salvataggio ? 'Salvo…' : 'Salva'}
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <h2 className="font-semibold text-gray-900 mb-4">Come funziona la vendita</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Ore per pagare
              </label>
              <input
                type="number" min="1" max="336" disabled={!modificabile}
                value={impostazioni.ore_validita_ordine}
                onChange={(e) => campo('ore_validita_ordine', Number(e.target.value))}
                className={classeInput}
              />
              <p className="text-xs text-gray-500 mt-1">
                Oltre questo termine i posti tornano in vendita. Attenzione alle 24 ore
                secche: un bonifico fatto venerdì sera risulta accreditato lunedì, e
                l'ordine scadrebbe con il cliente che ha già pagato. Un ordine singolo
                si può sempre prorogare a mano.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email dell'ufficio
              </label>
              <input
                type="email" disabled={!modificabile}
                value={impostazioni.email_ufficio || ''}
                onChange={(e) => campo('email_ufficio', e.target.value)}
                placeholder="info@casellanoleggiobus.it"
                className={classeInput}
              />
              <p className="text-xs text-gray-500 mt-1">
                Dove arriva l'avviso di ogni nuovo ordine da incassare. Vuoto: nessun avviso.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Firma delle email ai clienti
              </label>
              <input
                type="text" disabled={!modificabile}
                value={impostazioni.mittente_biglietti || ''}
                onChange={(e) => campo('mittente_biglietti', e.target.value)}
                placeholder="Casella Noleggio Bus"
                className={classeInput}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Condizioni di vendita
              </label>
              <textarea
                rows={5} disabled={!modificabile}
                value={impostazioni.testo_condizioni || ''}
                onChange={(e) => campo('testo_condizioni', e.target.value)}
                className={classeInput}
              />
              <p className="text-xs text-gray-500 mt-1">
                Il cliente le accetta prima di confermare e le ritrova nella email dei
                biglietti. Dal momento in cui il sito incassa, servono: fatele scrivere
                a chi segue gli aspetti legali, con la posizione sul diritto di recesso.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-900">Come si paga</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Si aggiungono e si tolgono da qui, senza passare dal programmatore.
              </p>
            </div>
            {modificabile && (
              <button
                onClick={aggiungiMetodo}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium shrink-0"
              >
                <Plus className="w-4 h-4" />
                Aggiungi
              </button>
            )}
          </div>

          {nessunMetodoAttivo && (
            <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              Nessun metodo attivo: il cliente può comprare, ma non riceve istruzioni
              per pagare e l'ufficio deve richiamarlo.
            </div>
          )}

          {metodi.length === 0 ? (
            <div className="py-10 text-center">
              <CreditCard className="w-9 h-9 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Nessun metodo di pagamento.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {metodi.map((metodo, indice) => (
                <RigaMetodo
                  key={metodo._k}
                  metodo={metodo}
                  modificabile={modificabile}
                  onSalva={(bozza) => salvaMetodo(metodo, bozza, indice)}
                  onElimina={() => eliminaMetodo(metodo)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const RigaMetodo = ({ metodo, modificabile, onSalva, onElimina }) => {
  const [bozza, setBozza] = useState(metodo);
  useEffect(() => { setBozza(metodo); }, [metodo]);

  const scrivi = (nome, valore) => setBozza((m) => ({ ...m, [nome]: valore }));
  const cambiato = CAMPI.some((n) => String(bozza[n] ?? '') !== String(metodo[n] ?? ''));
  const daSalvare = metodo.nuovo || cambiato;

  return (
    <div className={`rounded-lg border p-4 ${bozza.attivo ? 'border-gray-200' : 'border-dashed border-gray-300 bg-gray-50'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <label className="block text-xs text-gray-500 mb-1">Nome, come lo legge il cliente</label>
          <input
            type="text" value={bozza.nome || ''} disabled={!modificabile}
            onChange={(e) => scrivi('nome', e.target.value)}
            placeholder="Bonifico bancario"
            className={classeInput}
          />
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-6">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox" checked={!!bozza.attivo} disabled={!modificabile}
              onChange={(e) => scrivi('attivo', e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Attivo
          </label>
          {modificabile && (
            <button
              onClick={onElimina}
              className="p-1.5 text-gray-300 hover:text-red-600 transition"
              aria-label={metodo.nuovo ? 'Chiudi la scheda' : `Elimina ${metodo.nome}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tipo</label>
          <select
            value={bozza.tipo} disabled={!modificabile}
            onChange={(e) => scrivi('tipo', e.target.value)}
            className={classeInput}
          >
            {Object.entries(TIPI).map(([valore, etichetta]) => (
              <option key={valore} value={valore}>{etichetta}</option>
            ))}
          </select>
        </div>

        {bozza.tipo === 'BONIFICO' && (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Intestatario</label>
              <input
                type="text" value={bozza.intestatario || ''} disabled={!modificabile}
                onChange={(e) => scrivi('intestatario', e.target.value)}
                className={classeInput}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">IBAN</label>
              <input
                type="text" value={bozza.iban || ''} disabled={!modificabile}
                onChange={(e) => scrivi('iban', e.target.value.toUpperCase().replace(/\s/g, ''))}
                placeholder="IT60X0542811101000000123456"
                className={`${classeInput} font-mono`}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Causale</label>
              <input
                type="text" value={bozza.causale_template || ''} disabled={!modificabile}
                onChange={(e) => scrivi('causale_template', e.target.value)}
                className={`${classeInput} font-mono`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Segnaposto: <code>{'{codice_ordine}'}</code>, <code>{'{evento}'}</code>,{' '}
                <code>{'{cliente}'}</code>. Il codice ordine è l'unico modo per
                riconoscere un bonifico senza telefonare al cliente: non toglietelo.
              </p>
            </div>
          </>
        )}

        {bozza.tipo === 'LINK' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Indirizzo</label>
            <input
              type="url" value={bozza.url || ''} disabled={!modificabile}
              onChange={(e) => scrivi('url', e.target.value)}
              placeholder="https://…"
              className={classeInput}
            />
          </div>
        )}

        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Istruzioni per il cliente</label>
          <textarea
            rows={2} value={bozza.istruzioni || ''} disabled={!modificabile}
            onChange={(e) => scrivi('istruzioni', e.target.value)}
            placeholder="Cosa deve fare, scritto per lui."
            className={classeInput}
          />
        </div>
      </div>

      {modificabile && daSalvare && (
        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-amber-700 mr-auto">
            {metodo.nuovo ? 'Scheda nuova: esiste solo a schermo.' : 'Modifiche non salvate.'}
          </span>
          <button
            onClick={() => (metodo.nuovo ? onElimina() : setBozza(metodo))}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition"
          >
            Annulla
          </button>
          <button
            onClick={() => onSalva(bozza)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition text-sm font-medium"
          >
            <Save className="w-3.5 h-3.5" />
            Salva
          </button>
        </div>
      )}
    </div>
  );
};

/** Il primo messaggio leggibile dentro la risposta d'errore, a qualunque
 *  profondita' stia: un "[object Object]" a video non aiuta nessuno. */
const primoErrore = (errore) => {
  const cerca = (valore) => {
    if (!valore) return null;
    if (typeof valore === 'string') return valore;
    if (Array.isArray(valore)) {
      for (const voce of valore) {
        const trovato = cerca(voce);
        if (trovato) return trovato;
      }
      return null;
    }
    if (typeof valore === 'object') {
      if (valore.errore) return cerca(valore.errore);
      for (const nome of Object.keys(valore)) {
        const trovato = cerca(valore[nome]);
        if (trovato) return trovato;
      }
    }
    return null;
  };
  return cerca(errore.response?.data);
};

export default ImpostazioniEventi;
